import { Box, Typography } from "@mui/material";
import { type FC, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { FONT_SIZE, INK, LETTER_SPACING, NEUTRAL, SURFACE } from "../../../../theme/tokens";
import { RequestFilter } from "../../../RequestData/RequestsTab/utils";
import { type FlatSwitchOption, SystemFlatSwitch } from "../../../UIToolkit/Clickable/SystemFlatSwitch";
import { FilterPillButton } from "../../../UIToolkit/Filters/FilterPillButton";
import { GAPS_RESPONSE_FILTERS } from "./gapsOpenShiftsModel";
import { GapsOpenShiftsLoad } from "./useGapsOpenShiftsLoad";

type Props = {
	load: GapsOpenShiftsLoad;
	/** What each response option lists, over the requests every other filter already narrowed. */
	counts: Record<RequestFilter, number>;
	responseFilter: RequestFilter;
	onResponseFilter: (option: FlatSwitchOption<RequestFilter>) => void;
	/** A job / role-group or Day / Night filter is narrowing the list. */
	isNarrowed: boolean;
	onOpenFilter: () => void;
};

/** A no-break space: keeps the summary's line, and so the heading's height, while there is nothing to say. */
const BLANK_LINE = "\u00a0";

/** The response switch's labels, keyed by option. */
const RESPONSE_LABEL_KEY: Record<RequestFilter, string> = {
	[RequestFilter.All]: "gaps.requests.tab.all",
	[RequestFilter.WithResponse]: "gaps.requests.tab.answered",
	[RequestFilter.WithoutResponse]: "gaps.requests.tab.unanswered",
	[RequestFilter.ReplaceRequest]: "gaps.requests.tab.replace",
};

/**
 * Open Shifts' pinned heading (P3-12): the title with its one-line summary (how many are open, how many await a decision),
 * the Filter pill, and the response switch counting what each of its options lists. Nothing claims a figure before the
 * load has settled: the counts hold a dash, the summary says it is loading or stays blank on a failed load.
 */
export const GapsOpenShiftsHeader: FC<Props> = ({ load, counts, responseFilter, onResponseFilter, isNarrowed, onOpenFilter }) => {
	const { t } = useTranslation();
	const isLoaded = load === GapsOpenShiftsLoad.Ready;
	const awaitingCount = counts[RequestFilter.WithResponse];

	const switchOptions = useMemo<FlatSwitchOption<RequestFilter>[]>(
		() =>
			GAPS_RESPONSE_FILTERS.map((value) => ({
				value,
				"data-testid": `gaps-requests-filter-${value}`,
				// Label over count, the artifact's segmented control with its counts stacked so four fit a 320px phone.
				label: (
					<Box component="span" sx={{ display: "flex", flexDirection: "column", alignItems: "center", lineHeight: 1.15 }}>
						<Box component="span">{t(RESPONSE_LABEL_KEY[value])}</Box>
						<Box component="span" data-testid={`gaps-requests-filter-${value}-count`} sx={{ fontVariantNumeric: "tabular-nums", fontWeight: 700 }}>
							{/* A figure only once it is one; the dash holds the line so the switch does not change height. */}
							{isLoaded ? counts[value] : "–"}
						</Box>
					</Box>
				),
			})),
		[counts, isLoaded, t],
	);

	return (
		<Box data-testid="gaps-requests-pinned" sx={{ flex: "none", bgcolor: SURFACE, boxShadow: `0 1px 0 ${NEUTRAL[200]}`, position: "relative", zIndex: 2 }}>
			<Box sx={{ display: "flex", alignItems: "center", gap: 1, px: 2, pt: 1.5, pb: 1 }}>
				<Box sx={{ flex: 1, minWidth: 0 }}>
					<Typography component="h1" sx={{ m: 0, fontSize: FONT_SIZE.TITLE_MD, fontWeight: 700, letterSpacing: LETTER_SPACING.HEADLINE, color: INK.PRIMARY }}>
						{t("gaps.requests.title")}
					</Typography>
					<Typography data-testid="gaps-requests-summary" sx={{ fontSize: FONT_SIZE.CAPTION, color: INK.MUTED }}>
						{/* While it loads, say so; once it failed, the error below says it and nothing here claims a figure. */}
						{isLoaded
							? t("gaps.requests.summaryOpen", { count: counts[RequestFilter.All] })
							: load === GapsOpenShiftsLoad.Loading
								? t("gaps.requests.summaryPending")
								: BLANK_LINE}
						{isLoaded && awaitingCount > 0 && (
							<Box component="span" sx={{ color: "primary.dark", fontWeight: 600 }}>{` · ${t("gaps.requests.summaryAwaiting", { count: awaitingCount })}`}</Box>
						)}
					</Typography>
				</Box>
				{/* Named by its own text — "Filter", or "Filtered" once something is narrowed — never a fixed label over it. */}
				<FilterPillButton
					label={isNarrowed ? t("gaps.requests.filtered") : t("gaps.requests.filter")}
					isActive={isNarrowed}
					onClick={onOpenFilter}
					// Pinned chrome on a phone: the pill reaches 44px on touch.
					touchTarget
					dataTestId="gaps-requests-filter-open"
				/>
			</Box>
			<Box sx={{ px: 1.5, pb: 1.25 }}>
				<SystemFlatSwitch<RequestFilter>
					options={switchOptions}
					activeIndexOrValue={responseFilter}
					onSwitch={onResponseFilter}
					delayCallback={0}
					rootWidth="100%"
					rootMaxWidth="100%"
					// A segmented control across the screen: every option shares the width, so the indicator also follows the
					// track when the width changes (it re-measures on the options' own size).
					equalWidthOptions
					textWrap="nowrap"
					fontSizeOverrides={{ xs: FONT_SIZE.LABEL_SM }}
					// A phone screen's filter: each option reaches 44px on touch.
					touchTarget
					data-testid="gaps-requests-filter"
				/>
			</Box>
		</Box>
	);
};
